import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Collapse,
  Form,
  Image,
  Input,
  List,
  Tabs,
  Modal,
  Select,
  Empty,
  Space,
  Tag,
  Row,
  Col,
  Tooltip,
  Pagination,
  message,
} from 'antd';
import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageUploadField from '../../components/ImageUploadField';
import { useCms } from '../../store/context/CmsContext';
import { error, success } from '../../services/swal-mixin';

const { TabPane } = Tabs;
const { Panel } = Collapse;

const ContentManagement = ({ section }) => {
  const { siteData, updateSection, fetchCmsData, saveBlog, deleteBlog, savePartner, deletePartner, saveTestimonial } = useCms();
  const [form] = Form.useForm();
  const [blogForm] = Form.useForm();
  const [partnerForm] = Form.useForm();
  const [testimonialForm] = Form.useForm();
  const [ecosystemForm] = Form.useForm();
  const [stepForm] = Form.useForm();
  const [advantageForm] = Form.useForm();
  const [featureForm] = Form.useForm();
  const [aboutContentForm] = Form.useForm();
  const [faqForm] = Form.useForm();
  const [adminUserForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Testimonial states
  const [isTestimonialModalVisible, setIsTestimonialModalVisible] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  // Partner states
  const [isPartnerModalVisible, setIsPartnerModalVisible] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);

  // Blog states
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [isBlogModalVisible, setIsBlogModalVisible] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  // Ecosystem states
  const [isEcosystemModalVisible, setIsEcosystemModalVisible] = useState(false);
  const [editingEcosystem, setEditingEcosystem] = useState(null);

  // How-it-works states
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  // Advantages states
  const [isAdvantageModalVisible, setIsAdvantageModalVisible] = useState(false);
  const [editingAdvantage, setEditingAdvantage] = useState(null);

  // Features states
  const [isFeatureModalVisible, setIsFeatureModalVisible] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);

  const [isAboutContentModalVisible, setIsAboutContentModalVisible] = useState(false);
  const [editingAboutRow, setEditingAboutRow] = useState(null);

  // FAQ states
  const [isFaqModalVisible, setIsFaqModalVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [selectedFaqType, setSelectedFaqType] = useState(null); // 'personal' or 'business'

  // Admin User states
  const [isAdminUserModalVisible, setIsAdminUserModalVisible] = useState(false);
  const [editingAdminUser, setEditingAdminUser] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Dynamic Categories derived from blogs
  const categories = React.useMemo(() => {
    const blogCats = (siteData.blogs || []).map(b => b.category).filter(Boolean);
    const uniqueCats = Array.from(new Set(blogCats));
    return ['All', ...uniqueCats.sort()];
  }, [siteData.blogs]);

  // Sync form with context data when loaded
  React.useEffect(() => {
    if (siteData) {
      form.setFieldsValue({
        hero_headline: siteData.homeContent?.hero_headline,
        hero_subtext: siteData.homeContent?.hero_subtext,
        hero_image: siteData.homeContent?.hero_image,
        pill_text: siteData.homeContent?.pill_text,
        cbn_logo: siteData.settings?.cbn_logo,
        ndic_logo: siteData.settings?.ndic_logo,
        app_store_url: siteData.homeContent?.app_store_url,
        play_store_url: siteData.homeContent?.play_store_url,
        stats: siteData.homeContent?.stats || [],
        personal_heading: siteData.bankingDetails?.personal?.title,
        personal_body: siteData.bankingDetails?.personal?.description,
        personal_image: siteData.bankingDetails?.personal?.image_url,
        personal_bullets: siteData.bankingDetails?.personal?.bullets?.join('\n'),
        business_heading: siteData.bankingDetails?.business?.title,
        business_body: siteData.bankingDetails?.business?.description,
        business_image: siteData.bankingDetails?.business?.image_url,
        business_bullets: siteData.bankingDetails?.business?.bullets?.join('\n'),
        site_name: siteData.settings?.site_name,
        contact_email: siteData.settings?.contact_email,
        mission: siteData.settings?.mission,
        global_app_store_url: siteData.settings?.app_store_url,
        global_play_store_url: siteData.settings?.play_store_url,
        aboutIntro_title: siteData.aboutIntro?.title,
        aboutIntro_content: siteData.aboutIntro?.content,
        aboutIntro_pill: siteData.aboutIntro?.pill,
        aboutIntro_missionTitle: siteData.aboutIntro?.mission_title,
        aboutIntro_advantagesTitle: siteData.aboutIntro?.advantages_title,
        about_mission: siteData.settings?.mission,
      });
    }
  }, [siteData, form]);

  const handleEditEcosystem = (item) => {
    setEditingEcosystem(item);
    ecosystemForm.setFieldsValue(item);
    setIsEcosystemModalVisible(true);
  };

  const handleEcosystemSave = async () => {
    try {
      const values = await ecosystemForm.validateFields();
      setLoading(true);
      
      const updatedEcosystem = (siteData.ecosystem || []).map(item => 
        item.title === editingEcosystem.title ? { ...item, ...values } : item
      );

      await updateSection('ecosystem', updatedEcosystem);
      setIsEcosystemModalVisible(false);
      message.success('Ecosystem item updated!');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStep = (item) => {
    setEditingStep(item);
    stepForm.setFieldsValue(item);
    setIsStepModalVisible(true);
  };

  const handleStepSave = async () => {
    try {
      const values = await stepForm.validateFields();
      setLoading(true);

      const updatedSteps = (siteData.howItWorks?.steps || []).map(item => 
        item.title === editingStep.title ? { ...item, ...values } : item
      );

      await updateSection('howItWorks', { ...siteData.howItWorks, steps: updatedSteps });
      setIsStepModalVisible(false);
      message.success('Step updated successfully!');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdvantage = (item) => {
    setEditingAdvantage(item);
    advantageForm.setFieldsValue(item);
    setIsAdvantageModalVisible(true);
  };

  const handleAdvantageSave = async () => {
    try {
      const values = await advantageForm.validateFields();
      setLoading(true);
      const updated = (siteData.advantages || []).map(item => 
        item.title === editingAdvantage.title ? { ...item, ...values } : item
      );
      await updateSection('advantages', updated);
      setIsAdvantageModalVisible(false);
      message.success('Advantage updated!');
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleEditFeature = (item) => {
    setEditingFeature(item);
    featureForm.setFieldsValue(item);
    setIsFeatureModalVisible(true);
  };

  const handleFeatureSave = async () => {
    try {
      const values = await featureForm.validateFields();
      setLoading(true);
      const updated = (siteData.features || []).map(item => 
        item.title === editingFeature.title ? { ...item, ...values } : item
      );
      await updateSection('features', updated);
      setIsFeatureModalVisible(false);
      message.success('Feature updated!');
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleEditAboutRow = (item) => {
    setEditingAboutRow(item);
    aboutContentForm.setFieldsValue(item);
    setIsAboutContentModalVisible(true);
  };

  const handleAboutRowSave = async () => {
    try {
      const values = await aboutContentForm.validateFields();
      setLoading(true);
      const updated = (siteData.aboutContent || []).map(item => 
        item.title === editingAboutRow.title ? { ...item, ...values } : item
      );
      await updateSection('aboutContent', updated);
      setIsAboutContentModalVisible(false);
      message.success('About section row updated!');
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  // FAQ Handlers
  const handleEditFaq = (type, faq, index) => {
    setSelectedFaqType(type);
    setEditingFaq(faq);
    setEditingFaqIndex(index);
    faqForm.setFieldsValue(faq);
    setIsFaqModalVisible(true);
  };

  const handleFaqSave = async () => {
    try {
      const values = await faqForm.validateFields();
      setLoading(true);
      
      const currentFaqs = { ...siteData.faqs };
      const categoryFaqs = [...(currentFaqs[selectedFaqType] || [])];
      
      if (categoryFaqs[editingFaqIndex]) {
        categoryFaqs[editingFaqIndex] = { ...categoryFaqs[editingFaqIndex], ...values };
        currentFaqs[selectedFaqType] = categoryFaqs;
        
        await updateSection('faqs', currentFaqs);
        setIsFaqModalVisible(false);
        message.success(`${selectedFaqType.charAt(0).toUpperCase() + selectedFaqType.slice(1)} FAQ updated!`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdminUser = (item) => {
    setEditingAdminUser(item);
    adminUserForm.setFieldsValue(item);
    setIsAdminUserModalVisible(true);
  };

  const handleAdminUserSave = async () => {
    try {
      const values = await adminUserForm.validateFields();
      setLoading(true);

      let updatedAdmins = [...(siteData.admins || [])];
      if (editingAdminUser) {
        updatedAdmins = updatedAdmins.map(admin =>
          (admin.id === editingAdminUser.id || admin.email === editingAdminUser.email) ? { ...admin, ...values } : admin
        );
      } else {
        updatedAdmins.push({
          ...values,
          id: `a${Date.now()}`,
        });
      }

      await updateSection('admins', updatedAdmins);
      setIsAdminUserModalVisible(false);
      setEditingAdminUser(null);
      adminUserForm.resetFields();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdminUser = async (email) => {
    const adminToDelete = (siteData.admins || []).find(admin => admin.email === email);
    
    if (adminToDelete?.isProtected) {
      error('System Error', 'This is a default system administrator and cannot be removed.');
      return;
    }

    Modal.confirm({
      title: 'Are you sure you want to remove this admin?',
      content: 'They will no longer be able to log in using the bypass mechanism.',
      onOk: async () => {
        const updatedAdmins = (siteData.admins || []).filter(admin => admin.email !== email);
        await updateSection('admins', updatedAdmins);
      },
    });
  };

  const onFinish = async (values) => {
    setLoading(true);
    let updatedData = values;
    let targetKey = section;

    // Map UI section slugs to Context data keys
    if (section === 'home') targetKey = 'homeContent';
    if (section === 'banking') targetKey = 'bankingDetails';
    if (section === 'how_it_works') targetKey = 'howItWorks';
    if (section === 'cta_sections') targetKey = 'ctaSections';
    if (section === 'about') targetKey = 'aboutIntro';

    // Data Transformation for nested sections
    if (section === 'banking') {
      const current = siteData.bankingDetails || {};
      const isPersonal = values.personal_heading !== undefined;
      
      if (isPersonal) {
        updatedData = {
          ...current,
          personal: {
            ...current.personal,
            title: values.personal_heading,
            description: values.personal_body,
            image_url: values.personal_image,
            bullets: values.personal_bullets ? values.personal_bullets.split('\n').filter(b => b.trim()) : []
          }
        };
      } else {
        updatedData = {
          ...current,
          business: {
            ...current.business,
            title: values.business_heading,
            description: values.business_body,
            image_url: values.business_image,
            bullets: values.business_bullets ? values.business_bullets.split('\n').filter(b => b.trim()) : []
          }
        };
      }
    } else if (section === 'settings') {
      updatedData = {
        ...values,
        app_store_url: values.global_app_store_url,
        play_store_url: values.global_play_store_url,
      };
      delete updatedData.global_app_store_url;
      delete updatedData.global_play_store_url;
    } else if (section === 'about') {
      updatedData = {
        aboutIntro: {
          title: values.aboutIntro_title,
          content: values.aboutIntro_content,
          pill: values.aboutIntro_pill,
          mission_title: values.aboutIntro_missionTitle,
          advantages_title: values.aboutIntro_advantagesTitle,
        },
        aboutContent: siteData.aboutContent,
      };
      
      if (values.about_mission) {
        await updateSection('settings', { 
          ...siteData.settings, 
          mission: values.about_mission 
        });
      }
      
      // For about, we update bothaboutIntro and potentially settings, 
      // but the main section update below will handle aboutIntro via targetKey
    }

    await updateSection(targetKey, updatedData);
    setLoading(false);
  };

  const handleBlogSave = async () => {
    try {
      const values = await blogForm.validateFields();
      setLoading(true);
      
      const blogData = {
        ...values,
        id: editingBlog?.id || Date.now(),
      };
      
      await saveBlog(blogData);
      setIsBlogModalVisible(false);
      blogForm.resetFields();
      setEditingBlog(null);
    } catch (err) {
      console.error('Validate Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this blog post?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        await deleteBlog(id);
        setLoading(false);
      },
    });
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    blogForm.setFieldsValue(blog);
    setIsBlogModalVisible(true);
  };

  const handleCreateBlog = () => {
    setEditingBlog(null);
    blogForm.resetFields();
    blogForm.setFieldsValue({
      sections: [{ title: '', text: '' }],
      gallery: [],
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    });
    setIsBlogModalVisible(true);
  };

  // Partner Handlers
  const handleCreatePartner = () => {
    setEditingPartner(null);
    partnerForm.resetFields();
    setIsPartnerModalVisible(true);
  };

  const handleEditPartner = (partner) => {
    setEditingPartner(partner);
    partnerForm.setFieldsValue(partner);
    setIsPartnerModalVisible(true);
  };

  const handlePartnerSave = async () => {
    try {
      const values = await partnerForm.validateFields();
      setLoading(true);
      await savePartner({ ...values, id: editingPartner?.id });
      setIsPartnerModalVisible(false);
    } catch (err) {
      console.error('Validate Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const showPartnerDeleteConfirm = (partner) => {
    Modal.confirm({
      title: `Are you sure you want to delete ${partner.name}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This will remove the partner logo from the landing page.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        await deletePartner(partner.id || partner.name);
        setLoading(false);
      },
    });
  };

  // Testimonial Handlers
  const handleEditTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial);
    testimonialForm.setFieldsValue(testimonial);
    setIsTestimonialModalVisible(true);
  };

  const handleTestimonialSave = async () => {
    try {
      const values = await testimonialForm.validateFields();
      setLoading(true);
      await saveTestimonial({ ...values, id: editingTestimonial?.id });
      setIsTestimonialModalVisible(false);
    } catch (err) {
      console.error('Validate Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderHomeEditor = () => (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Card
        title={<span className="waya-text-grad">Hero Section</span>}
        className="mb-4 waya-glass-card"
        style={{ borderRadius: 'var(--waya-radius-lg)' }}
      >
        <Form.Item label="Pill Text (Top Badge)" name="pill_text">
          <Input placeholder="e.g. We are licensed by CBN and insured by NDIC" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <ImageUploadField
              label="CBN Logo"
              value={form.getFieldValue('cbn_logo')}
              onChange={(url) => form.setFieldsValue({ cbn_logo: url })}
            />
            <Form.Item name="cbn_logo" hidden><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <ImageUploadField
              label="NDIC Logo"
              value={form.getFieldValue('ndic_logo')}
              onChange={(url) => form.setFieldsValue({ ndic_logo: url })}
            />
            <Form.Item name="ndic_logo" hidden><Input /></Form.Item>
          </Col>
        </Row>
        
        <Form.Item label="Hero Headline" name="hero_headline" className="mt-3">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Hero Subtext" name="hero_subtext">
          <Input.TextArea rows={3} />
        </Form.Item>
        <ImageUploadField
          label="Hero Mockup Image"
          value={form.getFieldValue('hero_image')}
          onChange={(url) => form.setFieldsValue({ hero_image: url })}
        />
        <Form.Item name="hero_image" hidden>
          <Input />
        </Form.Item>
      </Card>

      <Card title="Mobile App Store Links" className="mb-4">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="App Store URL" name="app_store_url">
              <Input placeholder="iOS App URL" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Play Store URL" name="play_store_url">
              <Input placeholder="Android App URL" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Trust Metrics (Stats)" className="mb-4">
        <Form.List name="stats">
          {(fields) => (
            <Row gutter={16}>
              {fields.map(({ key, name, ...restField }) => (
                <Col span={8} key={key}>
                  <Card size="small" title={name === 0 ? "Active Users" : name === 1 ? "Processed" : "Uptime"}>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      label="Value"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'label']}
                      label="Label"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Form.List>
      </Card>

      <Card
        title="Support CTA"
        className="mb-4"
        headStyle={{ background: '#f8f9fa' }}
      >
        <Form.Item label="Support Headline" name="support_headline">
          <Input />
        </Form.Item>
        <Form.Item label="Support Body" name="support_body">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Card>

      <Button
        type="primary"
        icon={<SaveOutlined />}
        htmlType="submit"
        loading={loading}
        block
        size="large"
        className="waya-ant-btn-primary"
      >
        Save Home Page Content
      </Button>
    </Form>
  );

  const renderBankingEditor = () => (
    <Tabs defaultActiveKey="1" type="card">
      <TabPane tab="Personal Banking" key="1">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Card title="Personal Banking Info" className="mb-4">
            <Form.Item label="Title" name="personal_heading">
              <Input />
            </Form.Item>
            <Form.Item label="Description" name="personal_body">
              <Input.TextArea rows={3} />
            </Form.Item>
            <ImageUploadField
              label="Personal Banking Image"
              value={form.getFieldValue('personal_image')}
              onChange={(url) => form.setFieldsValue({ personal_image: url })}
            />
            <Form.Item name="personal_image" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Features (One per line)" name="personal_bullets">
              <Input.TextArea rows={5} placeholder="Bullet 1\nBullet 2..." />
            </Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={loading}
              className="waya-ant-btn-primary"
            >
              Save Personal Info
            </Button>
          </Card>
        </Form>
      </TabPane>
      <TabPane tab="Business Banking" key="2">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Card title="Business Banking Info" className="mb-4">
            <Form.Item label="Title" name="business_heading">
              <Input />
            </Form.Item>
            <Form.Item label="Description" name="business_body">
              <Input.TextArea rows={3} />
            </Form.Item>
            <ImageUploadField
              label="Business Banking Image"
              value={form.getFieldValue('business_image')}
              onChange={(url) => form.setFieldsValue({ business_image: url })}
            />
            <Form.Item name="business_image" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Features (One per line)" name="business_bullets">
              <Input.TextArea rows={5} placeholder="Bullet 1\nBullet 2..." />
            </Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={loading}
              className="waya-ant-btn-primary"
            >
              Save Business Info
            </Button>
          </Card>
        </Form>
      </TabPane>
    </Tabs>
  );

  const renderFaqEditor = () => (
    <Card title="Manage Website FAQs (Fixed: 5 Personal, 5 Business)" headStyle={{ background: '#f8f9fa' }}>
      <Collapse defaultActiveKey={['1']}>
        <Panel header="Personal Banking FAQs" key="1">
          <List
            dataSource={siteData.faqs?.personal || []}
            renderItem={(item, index) => (
              <List.Item
                actions={[
                  <Button 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => handleEditFaq('personal', item, index)}
                  >
                    Edit
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={item.question}
                  description={item.answer}
                />
              </List.Item>
            )}
          />
        </Panel>
        <Panel header="Business Banking FAQs" key="2">
          <List
            dataSource={siteData.faqs?.business || []}
            renderItem={(item, index) => (
              <List.Item
                actions={[
                  <Button 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => handleEditFaq('business', item, index)}
                  >
                    Edit
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={item.question}
                  description={item.answer}
                />
              </List.Item>
            )}
          />
        </Panel>
      </Collapse>

      <Modal
        title={<span className="waya-text-grad">Edit FAQ</span>}
        visible={isFaqModalVisible}
        onOk={handleFaqSave}
        onCancel={() => setIsFaqModalVisible(false)}
        confirmLoading={loading}
        okText="Update FAQ"
        className="waya-admin-modal"
      >
        <Form form={faqForm} layout="vertical">
          <Form.Item 
            name="question" 
            label="Question" 
            rules={[{ required: true, message: 'Please enter the question' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item 
            name="answer" 
            label="Answer" 
            rules={[{ required: true, message: 'Please enter the answer' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderTestimonialsEditor = () => (
    <Card 
      title={<span className="waya-text-grad">Manage Customer Testimonials (Fixed: 6)</span>} 
      headStyle={{ background: '#f8f9fa' }}
    >
      <List
        dataSource={siteData.testimonials || []}
        grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
        renderItem={(item) => (
          <List.Item>
            <Card 
              hoverable 
              size="small"
              actions={[
                <Tooltip title="Edit Content"><EditOutlined key="edit" onClick={() => handleEditTestimonial(item)} /></Tooltip>
              ]}
              style={{ borderRadius: '12px', height: '100%', border: '1px solid #eee' }}
            >
              <Card.Meta 
                title={item.name} 
                description={
                  <div style={{ minHeight: '80px' }}>
                    <Tag color="orange" className="mb-2">{item.source}</Tag>
                    <div style={{ fontSize: '13px', fontStyle: 'italic', color: '#666' }}>
                      "{item.text.length > 100 ? item.text.substring(0, 100) + '...' : item.text}"
                    </div>
                  </div>
                } 
              />
            </Card>
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">Edit Testimonial</span>}
        visible={isTestimonialModalVisible}
        onOk={handleTestimonialSave}
        onCancel={() => setIsTestimonialModalVisible(false)}
        confirmLoading={loading}
        okText="Update Testimonial"
        className="waya-admin-modal"
      >
        <Form form={testimonialForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Customer Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="source" label="Source (e.g. App Store)" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="text" label="Testimonial Text" rules={[{ required: true }]}>
            <Input.TextArea rows={4} maxLength={300} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderEcosystemEditor = () => (
    <Card title="Manage Waya Ecosystem" headStyle={{ background: '#f8f9fa' }}>
      <List
        dataSource={siteData.ecosystem || []}
        renderItem={(item, index) => (
          <List.Item actions={[<Button type="link" icon={<EditOutlined />} onClick={() => handleEditEcosystem(item)} />]}>
            <List.Item.Meta
              avatar={
                item.image_url && <Image src={item.image_url} width={40} />
              }
              title={item.title}
              description={item.description}
            />
            <div style={{ color: '#ff4400' }}>{item.type}</div>
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">Edit Ecosystem Item</span>}
        visible={isEcosystemModalVisible}
        onOk={handleEcosystemSave}
        onCancel={() => setIsEcosystemModalVisible(false)}
        confirmLoading={loading}
        okText="Save Changes"
        className="waya-admin-modal"
      >
        <Form form={ecosystemForm} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Category/Type" rules={[{ required: true }]}>
            <Input placeholder="e.g. Core Banking" />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <ImageUploadField
            label="Icon/Image"
            value={ecosystemForm.getFieldValue('image_url')}
            onChange={(url) => ecosystemForm.setFieldsValue({ image_url: url })}
          />
          <Form.Item name="image_url" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderPartnersEditor = () => (
    <Card
      title={<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Manage Partners & Regulators</span>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleCreatePartner}
          className="waya-ant-btn-primary"
        >
          Add Partner
        </Button>
      </div>}
      headStyle={{ background: '#f8f9fa' }}
    >
      <List
        dataSource={siteData.partners || []}
        grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
        renderItem={(item, index) => (
          <List.Item>
            <Card 
              hoverable 
              actions={[
                <Tooltip title="Edit"><EditOutlined key="edit" onClick={() => handleEditPartner(item)} /></Tooltip>,
                <Tooltip title="Delete"><DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => showPartnerDeleteConfirm(item)} /></Tooltip>
              ]}
              cover={
                <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: '#f5f5f5' }}>
                  <img alt={item.name} src={item.image_url} style={{ maxHeight: '100%', maxWidth: '100%' }} />
                </div>
              }
            >
              <Card.Meta title={item.name} description={item.type} />
            </Card>
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">{editingPartner ? 'Edit Partner' : 'Add New Partner'}</span>}
        visible={isPartnerModalVisible}
        onOk={handlePartnerSave}
        onCancel={() => setIsPartnerModalVisible(false)}
        confirmLoading={loading}
        okText="Save Partner"
        cancelText="Cancel"
        className="waya-admin-modal"
      >
        <Form form={partnerForm} layout="vertical">
          <Form.Item name="name" label="Partner Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Type / Category" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="regulator">Regulator (e.g. CBN, NDIC)</Select.Option>
              <Select.Option value="scheme">Payment Scheme (e.g. Mastercard, Visa)</Select.Option>
              <Select.Option value="tech">Technology Partner</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <div style={{ marginTop: '10px' }}>
            <ImageUploadField
              label="Partner Logo"
              value={partnerForm.getFieldValue('image_url')}
              onChange={(url) => partnerForm.setFieldsValue({ image_url: url })}
            />
            <Form.Item name="image_url" hidden rules={[{ required: true, message: 'Logo is required' }]}>
              <Input />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Card>
  );

  const renderBlogsEditor = () => {
    const filteredBlogs = (siteData.blogs || []).filter(blog => {
      const matchesSearch = blog.title?.toLowerCase().includes(searchText.toLowerCase()) || 
                           blog.excerpt?.toLowerCase().includes(searchText.toLowerCase());
      const matchesCategory = filterCategory === 'All' || blog.category === filterCategory;
      return matchesSearch && matchesCategory;
    });

    const paginatedBlogs = filteredBlogs.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );

    return (
      <div className="animate__animated animate__fadeIn">
        <Card 
          className="waya-glass-card mb-4"
          style={{ borderRadius: 'var(--waya-radius-lg)', border: 'none' }}
        >
          <Row gutter={16} align="middle">
            <Col xs={24} md={12} lg={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Search blogs..."
                value={searchText}
                onChange={e => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
                allowClear
                size="large"
                style={{ borderRadius: '12px' }}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Select
                suffixIcon={<FilterOutlined style={{ color: '#bfbfbf' }} />}
                style={{ width: '100%' }}
                placeholder="Category"
                value={filterCategory}
                onChange={val => {
                  setFilterCategory(val);
                  setCurrentPage(1);
                }}
                size="large"
              >
                {categories.map(cat => (
                  <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} lg={8} style={{ textAlign: 'right' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreateBlog}
                size="large"
                className="waya-ant-btn-primary"
                style={{ borderRadius: '12px', height: 'auto', padding: '8px 24px' }}
              >
                Create New Post
              </Button>
            </Col>
          </Row>
        </Card>

        {filteredBlogs.length > 0 ? (
          <div className="waya-blogs-list-container">
            <Row gutter={[24, 24]}>
              {paginatedBlogs.map(item => (
                <Col xs={24} sm={12} xl={8} key={item.id}>
                  <Card
                    hoverable
                    className="waya-blog-card"
                    style={{ 
                      borderRadius: 'var(--waya-radius-lg)', 
                      overflow: 'hidden',
                      border: '1px solid rgba(255, 68, 0, 0.1)'
                    }}
                    cover={
                      <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                        <img
                          alt={item.title}
                          src={item.featuredImage}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Tag 
                          color="orange" 
                          style={{ 
                            position: 'absolute', 
                            top: 12, 
                            left: 12, 
                            borderRadius: '8px',
                            backdropFilter: 'blur(4px)',
                            border: 'none',
                            background: 'rgba(255, 68, 0, 0.8)',
                            color: '#fff'
                          }}
                        >
                          {item.category}
                        </Tag>
                      </div>
                    }
                    actions={[
                      <Tooltip title="Edit Post">
                        <EditOutlined key="edit" onClick={() => handleEditBlog(item)} />
                      </Tooltip>,
                      <Tooltip title="Delete Post">
                        <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => showDeleteConfirm(item.id)} />
                      </Tooltip>,
                    ]}
                  >
                    <Card.Meta
                      title={<div style={{ whiteSpace: 'normal', height: '3em', overflow: 'hidden' }}>{item.title}</div>}
                      description={
                        <div>
                          <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: '8px' }}>
                            {item.date} • {item.readTime}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#595959',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {item.excerpt}
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredBlogs.length}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
                className="waya-custom-pagination"
              />
            </div>
          </div>
        ) : (
          <Empty 
            description="No blogs found matching your criteria" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '60px', background: 'rgba(255,255,255,0.5)', borderRadius: '20px' }}
          />
        )}

        <Modal
          title={<span className="waya-text-grad">{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</span>}
          visible={isBlogModalVisible}
          onOk={handleBlogSave}
          onCancel={() => setIsBlogModalVisible(false)}
          width={1000}
          confirmLoading={loading}
          okText="Save changes"
          cancelText="Discard"
          bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '24px' }}
          className="waya-admin-modal"
          centered
        >
          <Form form={blogForm} layout="vertical">
            <Tabs defaultActiveKey="1" className="waya-custom-tabs">
              <TabPane tab="Basic Information" key="1">
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item label="Blog Title" name="title" rules={[{ required: true }]}>
                      <Input size="large" />
                    </Form.Item>
                    <Form.Item label="Excerpt / Summary" name="excerpt" rules={[{ required: true }]}>
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                      <Select 
                        size="large" 
                        showSearch 
                        placeholder="Select or type new category"
                        mode="tags"
                        maxCount={1}
                        style={{ width: '100%' }}
                      >
                        {categories.filter(cat => cat !== 'All').map(cat => (
                          <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item label="Read Time" name="readTime">
                      <Input placeholder="e.g. 5 min read" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="Author Details" size="small" className="mb-3">
                      <Form.Item label="Author Name" name="author">
                        <Input />
                      </Form.Item>
                      <Form.Item label="Author Role" name="authorRole">
                        <Input />
                      </Form.Item>
                      <Form.Item label="Author Avatar (URL)" name="authorAvatar">
                        <Input />
                      </Form.Item>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Media" size="small">
                      <ImageUploadField
                        label="Featured Image"
                        value={blogForm.getFieldValue('featuredImage')}
                        onChange={(url) => blogForm.setFieldsValue({ featuredImage: url })}
                      />
                      <Form.Item name="featuredImage" hidden>
                        <Input />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tab="Content Sections" key="2">
                <Form.List name="sections">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card 
                          key={key} 
                          className="mb-4 shadow-sm"
                          title={`Section ${name + 1}`}
                          extra={<Button type="link" danger onClick={() => remove(name)} icon={<DeleteOutlined />} />}
                          style={{ borderRadius: '12px' }}
                        >
                          <Form.Item
                            {...restField}
                            name={[name, 'title']}
                            label="Section Title"
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'text']}
                            label="Content Body"
                            rules={[{ required: true }]}
                          >
                            <ReactQuill theme="snow" style={{ height: '300px', marginBottom: '45px' }} />
                          </Form.Item>
                          <div style={{ marginTop: '10px' }}>
                            <Form.Item label="Section Image (Optional)">
                              <ImageUploadField
                                value={blogForm.getFieldValue(['sections', name, 'image'])}
                                onChange={(url) => {
                                  const currentSections = blogForm.getFieldValue('sections');
                                  currentSections[name].image = url;
                                  blogForm.setFieldsValue({ sections: currentSections });
                                }}
                              />
                              <Form.Item {...restField} name={[name, 'image']} hidden>
                                <Input />
                              </Form.Item>
                            </Form.Item>
                          </div>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ height: '45px', borderRadius: '12px' }}>
                        Add New Section
                      </Button>
                    </>
                  )}
                </Form.List>
              </TabPane>

              <TabPane tab="Gallery" key="3">
                <Form.List name="gallery">
                  {(fields, { add, remove }) => (
                    <Row gutter={[16, 16]}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Col span={8} key={key}>
                          <Card 
                            size="small" 
                            cover={
                              <div style={{ padding: '8px' }}>
                                <img 
                                  src={blogForm.getFieldValue(['gallery', name])} 
                                  style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                                  alt=""
                                />
                              </div>
                            }
                            actions={[<DeleteOutlined key="del" onClick={() => remove(name)} style={{ color: 'red' }} />]}
                          >
                            <Form.Item {...restField} name={name} noStyle>
                              <Input placeholder="Image URL" />
                            </Form.Item>
                          </Card>
                        </Col>
                      ))}
                      <Col span={8}>
                        <Button 
                          type="dashed" 
                          onClick={() => add()} 
                          block 
                          icon={<PlusOutlined />} 
                          style={{ height: '160px', borderRadius: '12px' }}
                        >
                          Add Image
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form.List>
              </TabPane>
            </Tabs>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderAdvantagesEditor = () => (
    <Card title="Core Advantages" headStyle={{ background: '#f8f9fa' }}>
      <List
        dataSource={siteData.advantages || []}
        renderItem={(item, index) => (
          <List.Item actions={[<Button type="link" icon={<EditOutlined />} onClick={() => handleEditAdvantage(item)} />]}>
            <List.Item.Meta title={item.title} description={item.desc} />
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">Edit Advantage</span>}
        visible={isAdvantageModalVisible}
        onOk={handleAdvantageSave}
        onCancel={() => setIsAdvantageModalVisible(false)}
        confirmLoading={loading}
        okText="Update Advantage"
        className="waya-admin-modal"
      >
        <Form form={advantageForm} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="desc" label="Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderHowItWorksEditor = () => (
    <Card title="Onboarding Steps" headStyle={{ background: '#f8f9fa' }}>
      <List
        dataSource={siteData.howItWorks?.steps || []}
        renderItem={(item, index) => (
          <List.Item actions={[<Button type="link" icon={<EditOutlined />} onClick={() => handleEditStep(item)} />]}>
            <List.Item.Meta
              avatar={
                item.image_url && <Image src={item.image_url} width={40} />
              }
              title={`${index + 1}. ${item.title}`}
              description={item.description}
            />
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">Edit Step {editingStep?.title}</span>}
        visible={isStepModalVisible}
        onOk={handleStepSave}
        onCancel={() => setIsStepModalVisible(false)}
        confirmLoading={loading}
        okText="Update Step"
        className="waya-admin-modal"
      >
        <Form form={stepForm} layout="vertical">
          <Form.Item name="title" label="Step Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Step Description" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <ImageUploadField
            label="Step Image"
            value={stepForm.getFieldValue('image_url')}
            onChange={(url) => stepForm.setFieldsValue({ image_url: url })}
          />
          <Form.Item name="image_url" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderFeaturesEditor = () => (
    <Card title="Primary Features" headStyle={{ background: '#f8f9fa' }}>
      <List
        dataSource={siteData.features || []}
        renderItem={(item, index) => (
          <List.Item actions={[<Button type="link" icon={<EditOutlined />} onClick={() => handleEditFeature(item)} />]}>
            <List.Item.Meta title={item.title} description={item.body} />
          </List.Item>
        )}
      />

      <Modal
        title={<span className="waya-text-grad">Edit Feature</span>}
        visible={isFeatureModalVisible}
        onOk={handleFeatureSave}
        onCancel={() => setIsFeatureModalVisible(false)}
        confirmLoading={loading}
        okText="Update Feature"
        className="waya-admin-modal"
      >
        <Form form={featureForm} layout="vertical">
          <Form.Item name="title" label="Feature Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Feature Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderAboutEditor = () => (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Card
        title={<span className="waya-text-grad">About Us Hero (Header)</span>}
        className="mb-4 waya-glass-card"
        style={{ borderRadius: 'var(--waya-radius-lg)' }}
      >
        <Form.Item label="Badge Text (Pill)" name="aboutIntro_pill">
          <Input placeholder="e.g. About Us" />
        </Form.Item>
        <Form.Item label="Hero Title" name="aboutIntro_title">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Intro Body Text" name="aboutIntro_content">
          <Input.TextArea rows={4} />
        </Form.Item>
      </Card>

      <Card
        title="Our Mission Statement"
        className="mb-4"
        headStyle={{ background: '#f8f9fa' }}
      >
        <Form.Item label="Mission Section Title" name="aboutIntro_missionTitle">
          <Input placeholder="e.g. Our Mission" />
        </Form.Item>
        <Form.Item label="Mission Content" name="about_mission">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Card>

      <Card
        title="Feature Rows (Hustlers & Builders)"
        className="mb-4"
        headStyle={{ background: '#f8f9fa' }}
      >
        <List
          dataSource={siteData.aboutContent || []}
          renderItem={(item, index) => (
            <List.Item actions={[<Button type="link" icon={<EditOutlined />} onClick={() => handleEditAboutRow(item)} />]}>
              <List.Item.Meta
                avatar={
                  item.image_url && <Image src={item.image_url} width={40} />
                }
                title={item.title}
                description={item.content}
              />
              <div style={{ color: '#666', fontSize: '12px' }}>
                Tag: {item.tag}
              </div>
            </List.Item>
          )}
        />

        <Modal
          title={<span className="waya-text-grad">Edit About Feature Row</span>}
          visible={isAboutContentModalVisible}
          onOk={handleAboutRowSave}
          onCancel={() => setIsAboutContentModalVisible(false)}
          confirmLoading={loading}
          okText="Update Row"
          className="waya-admin-modal"
        >
          <Form form={aboutContentForm} layout="vertical">
            <Form.Item name="title" label="Row Title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="content" label="Row Content" rules={[{ required: true }]}>
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item name="tag" label="Header Tag" rules={[{ required: true }]}>
              <Input placeholder="e.g. For SMEs & Businesses" />
            </Form.Item>
            <ImageUploadField
              label="Section Image"
              value={aboutContentForm.getFieldValue('image_url')}
              onChange={(url) => aboutContentForm.setFieldsValue({ image_url: url })}
            />
            <Form.Item name="image_url" hidden>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Card>

      <Card
        title="WayaBank Advantages"
        className="mb-4"
        headStyle={{ background: '#f8f9fa' }}
        extra={<Button type="link" onClick={() => window.location.href = '#/management/cms/advantages'}>Manage Advantages Sub-section</Button>}
      >
        <Form.Item label="Advantages Section Title" name="aboutIntro_advantagesTitle">
          <Input placeholder="e.g. The WayaBank Advantage..." />
        </Form.Item>
        <List
          dataSource={siteData.advantages || []}
          size="small"
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta title={item.title} description={item.desc} />
            </List.Item>
          )}
        />
      </Card>

      <Button
        type="primary"
        icon={<SaveOutlined />}
        htmlType="submit"
        loading={loading}
        block
        size="large"
        className="waya-ant-btn-primary"
      >
        Save About Us Content
      </Button>
    </Form>
  );

  const renderCtaEditor = () => (
    <Card title="Call-to-Action Sections" headStyle={{ background: '#f8f9fa' }}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Collapse defaultActiveKey={['1']}>
          <Panel header="Support Section" key="1">
            <Form.Item
              label="Support Title"
              name={['ctaSections', 'support', 'title']}
              initialValue={siteData.ctaSections?.support?.title}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Support Subline"
              name={['ctaSections', 'support', 'subline']}
              initialValue={siteData.ctaSections?.support?.subline}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </Panel>
          <Panel header="About Section (Joined Revolution)" key="2">
            <Form.Item
              label="About Title"
              name={['ctaSections', 'about', 'title']}
              initialValue={siteData.ctaSections?.about?.title}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="About Subline"
              name={['ctaSections', 'about', 'subline']}
              initialValue={siteData.ctaSections?.about?.subline}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          </Panel>
        </Collapse>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          htmlType="submit"
          loading={loading}
          className="mt-4"
          block
        >
          Save CTA Content
        </Button>
      </Form>
    </Card>
  );

  const renderSettingsEditor = () => (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Card title="General Site Settings" headStyle={{ background: '#f8f9fa' }}>
        <Form.Item label="Site Name" name="site_name">
          <Input />
        </Form.Item>
        <Form.Item label="Contact Email" name="contact_email">
          <Input />
        </Form.Item>
        <Form.Item label="Mission Statement" name="mission">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Global App Store URL" name="global_app_store_url">
              <Input placeholder="iOS App URL" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Global Play Store URL" name="global_play_store_url">
              <Input placeholder="Android App URL" />
            </Form.Item>
          </Col>
        </Row>

        <div className="row">
          <div className="col-12 mt-3">
             <Form.Item 
                label={
                  <Space>
                    B2B Documentation (Markdown)
                    <Tooltip title="This content will appear on the /merchant-documentation page. Supports standard Markdown.">
                      <ExclamationCircleOutlined />
                    </Tooltip>
                  </Space>
                } 
                name="b2b_documentation"
              >
                <Input.TextArea rows={15} placeholder="Paste your markdown documentation here..." />
             </Form.Item>
          </div>
          <div className="col-md-6">
            <ImageUploadField
              label="CBN Logo"
              value={form.getFieldValue('cbn_logo')}
              onChange={(url) => form.setFieldsValue({ cbn_logo: url })}
            />
            <Form.Item name="cbn_logo" hidden>
              <Input />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <ImageUploadField
              label="NDIC Logo"
              value={form.getFieldValue('ndic_logo')}
              onChange={(url) => form.setFieldsValue({ ndic_logo: url })}
            />
            <Form.Item name="ndic_logo" hidden>
              <Input />
            </Form.Item>
          </div>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          htmlType="submit"
          loading={loading}
        >
          Save Settings
        </Button>
      </Card>
    </Form>
  );

  const renderAdminUsersEditor = () => (
    <Card 
      title="Administrator Access Management" 
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => {
            setEditingAdminUser(null);
            adminUserForm.resetFields();
            setIsAdminUserModalVisible(true);
          }}
        >
          Add Admin User
        </Button>
      }
    >
      <List
        dataSource={siteData.admins || []}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button icon={<EditOutlined />} onClick={() => handleEditAdminUser(item)}>Edit</Button>,
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={() => handleDeleteAdminUser(item.email)}
                disabled={item.isProtected}
              >
                Remove
              </Button>,
            ]}
          >
            <List.Item.Meta
              avatar={<Tag color="orange" style={{ borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>{item.fullName?.charAt(0) || 'A'}</Tag>}
              title={
                <Space>
                  <span style={{ fontWeight: 600 }}>{item.fullName}</span>
                  {item.isProtected && <Tag color="blue" bordered={false}>System Account</Tag>}
                </Space>
              }
              description={
                <Space direction="vertical" size={0}>
                  <Tag color="cyan">{item.email}</Tag>
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>Password: ••••••••</span>
                </Space>
              }
            />
          </List.Item>
        )}
      />

      <Modal
        title={editingAdminUser ? 'Edit Administrator' : 'Add New Administrator'}
        visible={isAdminUserModalVisible}
        onOk={handleAdminUserSave}
        onCancel={() => setIsAdminUserModalVisible(false)}
        okText="Save Administrator"
        confirmLoading={loading}
      >
        <Form form={adminUserForm} layout="vertical">
          <Form.Item
            label="Full Name"
            name="fullName"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g. John Doe" />
          </Form.Item>
          <Form.Item
            label="Email/Username"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="e.g. admin@wayapaychat.com" disabled={!!editingAdminUser} />
          </Form.Item>
          <Form.Item
            label="Login Password"
            name="password"
            rules={[{ required: true, message: 'Please enter a password' }]}
          >
            <Input.Password placeholder="Admin password" />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
          >
            <Input placeholder="e.g. 234..." />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const renderContent = () => {
    switch (section) {
      case 'home':
        return renderHomeEditor();
      case 'banking':
        return renderBankingEditor();
      case 'faqs':
        return renderFaqEditor();
      case 'testimonials':
        return renderTestimonialsEditor();
      case 'ecosystem':
        return renderEcosystemEditor();
      case 'partners':
        return renderPartnersEditor();
      case 'blogs':
        return renderBlogsEditor();
      case 'advantages':
        return renderAdvantagesEditor();
      case 'how_it_works':
        return renderHowItWorksEditor();
      case 'features':
        return renderFeaturesEditor();
      case 'about':
        return renderAboutEditor();
      case 'cta_sections':
        return renderCtaEditor();
      case 'settings':
        return renderSettingsEditor();
      case 'admins':
        return renderAdminUsersEditor();
      case 'overview':
        return (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <EditOutlined
              style={{
                fontSize: '64px',
                color: '#ff4400',
                marginBottom: '24px',
                opacity: 0.8
              }}
            />
            <h2 className="waya-text-grad" style={{ fontSize: '32px' }}>WayaBank CMS Command Center</h2>
            <p style={{ color: '#666', fontSize: '18px', maxWidth: '600px', margin: '0 auto' }}>
              Select a section from the persistent sidebar to manage dynamic headlines, 
              assets, and core data for the WayaBank ecosystem.
            </p>
          </div>
        );
      default:
        return <div>Select a sub-section to manage.</div>;
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1
        style={{
          color: '#ff4400',
          marginBottom: '24px',
          textTransform: 'capitalize',
        }}
      >
        {section.replace('_', ' ')} Management
      </h1>
      {renderContent()}
    </div>
  );
};

export default ContentManagement;
